import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import produce from 'immer';
import useQueryParamState from './useQueryParamState';

interface ITableOptionProps {
  filterOptionModel: Record<string, string | number | null>;
  sortOptionModel: {
    sort: string;
  };
  paginationModel: {
    pageIndex: number;
    pageSize: number;
  };
}

interface IUserReactTableReturnTypes {
  filterOption: Record<string, string | number | null>;
  paginationOption: {
    pageIndex: number;
    pageSize: number;
  };
  sortOption: {
    sort: string;
  };
  handleChangeFilterOption: (e: { value: string; id: string }) => void;
  handleChangePaginationOption: (id: string, value: number) => void;
  handleClickSortOption: (columnId: string, isMultiSortable: boolean) => void;
  handleClickResetFilter: () => void;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  handleDateRangeChange: (dateRange: { startDate: string; endDate: string }) => void;
}

const useReactTable = ({
  filterOptionModel,
  sortOptionModel,
  paginationModel,
}: ITableOptionProps): IUserReactTableReturnTypes => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const [filterOption, setFilterOption] = useQueryParamState(filterOptionModel);
  const [paginationOption, setPaginationOption] = useQueryParamState(paginationModel);
  const [sortOption, setSortOption] = useQueryParamState(sortOptionModel);
  const [currentPage, setCurrentPage] = useState(paginationOption.pageIndex);

  const handleClickSortOption = (columnId: string, isMultiSortable: boolean) => {
    setSortOption(
      produce(sortOption, (draft) => {
        if (isMultiSortable) {
          const sortedColumns = draft.sort ? draft.sort.split(',') : [];
          const isSortedAsc = sortedColumns.includes(columnId);
          const isSortedDesc = sortedColumns.includes(`-${columnId}`);

          if (isSortedDesc) {
            const index = sortedColumns.indexOf(`-${columnId}`);
            sortedColumns.splice(index, 1);
            sortedColumns.push(columnId);
          } else if (isSortedAsc) {
            const index = sortedColumns.indexOf(columnId);
            sortedColumns.splice(index, 1);
          } else {
            sortedColumns.push(`-${columnId}`);
          }
          draft.sort = sortedColumns.join(',');
        } else {
          if (!draft.sort || draft.sort === columnId) {
            draft.sort = `-${columnId}`;
          } else if (draft.sort === `-${columnId}`) {
            draft.sort = columnId;
          } else {
            draft.sort = '';
          }
        }
      }),
    );
  };

  const handleChangeFilterOption = (e: { value: string; id: string }) => {
    const { value, id } = e;
    setFilterOption({
      [id]: value,
      ...paginationModel,
    });
    setCurrentPage(1);
  };

  const handleChangePaginationOption = (id: string, value: number) => {
    const baseValue = id === 'pageSize' ? 100 : 1;
    const adjustedValue = Math.max(value, baseValue);
    if (id === 'pageSize') {
      setPaginationOption({ pageSize: adjustedValue, pageIndex: 1 });
      setCurrentPage(1);
    } else {
      setPaginationOption({ [id]: adjustedValue });
      setCurrentPage(value);
    }
  };

  const handleDateRangeChange = (dateRange: { startDate: string; endDate: string }) => {
    setFilterOption({
      createStartDate: dateRange.startDate,
      createEndDate: dateRange.endDate,
    });
    setCurrentPage(1);
  };

  const handleClickResetFilter = () => {
    const initialSearch = queryString.stringify(paginationModel, {
      skipNull: true,
      skipEmptyString: true,
    });
    navigate({
      pathname,
      search: initialSearch,
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    const adjustedPaginationOption = {
      pageIndex: Math.max(paginationOption.pageIndex, 1),
      pageSize: Math.max(paginationOption.pageSize, 100),
    };
    setPaginationOption({
      ...adjustedPaginationOption,
      ...sortOption,
      ...filterOption,
    });
  }, [navigate, pathname]);

  return {
    filterOption,
    paginationOption,
    sortOption,
    handleChangeFilterOption,
    handleChangePaginationOption,
    handleClickSortOption,
    handleClickResetFilter,
    currentPage,
    setCurrentPage,
    handleDateRangeChange,
  };
};

export default useReactTable;
